// Tipos públicos de la simulación de vuelo. Ver docs/theory/simulation.md.
import type { Atmosphere } from '../atmosphere/isa';
import type { GravityModel } from '../gravity/gravity';
import type { LaunchConditions } from '../launch/launch';
import type { Motor } from '../motor/motor';
import type { Rocket } from '../rocket/types';
import type { WindModel } from '../wind/wind';

export type EventType =
  | 'launch'
  | 'ignition'
  | 'liftoff'
  | 'launch-rod-clearance'
  | 'burnout'
  | 'ejection-charge'
  | 'apogee'
  | 'recovery-device-deployment'
  | 'tumble'
  | 'ground-hit'
  | 'simulation-end';

export interface FlightEvent {
  readonly type: EventType;
  /** Tiempo (s). */
  readonly time: number;
  /** Altitud sobre el sitio de lanzamiento (m). */
  readonly altitude: number;
  /** Rapidez en el evento (m/s), cuando aplica. */
  readonly velocity?: number;
  readonly detail?: string;
}

export type SimulationStatus = 'completed' | 'no-liftoff' | 'max-time' | 'cancelled' | 'error';
export type FlightPhase = 'pad' | 'rod' | 'flight' | 'recovery' | 'tumble' | 'landed';

export type MotorLookup = (motorId: string) => Motor | undefined;

export interface SimulationConfig {
  readonly rocket: Rocket;
  readonly motors: MotorLookup;
  readonly launch: LaunchConditions;
  /** Viento; por defecto, aire en calma. */
  readonly wind?: WindModel;
  /** Atmósfera; por defecto ISA ajustada al sitio de lanzamiento (útil para pruebas con vacío). */
  readonly atmosphere?: Atmosphere;
  /** Gravedad; por defecto constante (9,80665 m/s²). */
  readonly gravity?: GravityModel;
  /** Altura de rugosidad de la superficie (m). Por defecto, liso (10 µm). */
  readonly roughness?: number;
  /** Paso de tiempo del usuario (s). Por defecto 0,05. */
  readonly timeStep?: number;
  /** Giro máximo por paso (rad). Por defecto 2°. */
  readonly maxAngleStep?: number;
  /** Tiempo máximo de simulación (s). Por defecto 1200. */
  readonly maxTime?: number;
  /** Ángulo de pérdida (rad). */
  readonly stallAngle?: number;
  /** Semilla de la perturbación de cabeceo y guiñada. Por defecto 0. */
  readonly seed?: number;
  /** Amplitud de la perturbación (coeficiente de momento); 0 la desactiva. Por defecto 1e-3. */
  readonly perturbation?: number;
}

export interface FlightSeries {
  readonly time: number[];
  /** Altitud sobre el sitio de lanzamiento (m). */
  readonly altitude: number[];
  /** Desplazamiento este y norte desde el sitio (m). */
  readonly east: number[];
  readonly north: number[];
  readonly verticalVelocity: number[];
  readonly speed: number[];
  /** Módulo de la aceleración (m/s²). */
  readonly acceleration: number[];
  readonly mach: number[];
  /** Ángulo de ataque (rad); NaN en recuperación y volteo. */
  readonly angleOfAttack: number[];
  readonly thrust: number[];
  /** Fuerza de arrastre axial (N). */
  readonly drag: number[];
  readonly mass: number[];
  /** Posición del CG desde la punta (m). */
  readonly cgX: number[];
  /** Posición del CP desde la punta (m); NaN sin velocidad o en recuperación y volteo. */
  readonly cpX: number[];
  /** Coeficiente de arrastre axial; NaN en recuperación y volteo. */
  readonly dragCoefficient: number[];
  /** Margen de estabilidad en calibres; se conserva el último valor en recuperación y volteo. */
  readonly stabilityMargin: number[];
}

export interface FlightSummary {
  /** Altitud máxima sobre el sitio (m). */
  readonly apogee: number;
  readonly timeToApogee: number;
  /** Velocidad máxima (m/s). */
  readonly maxVelocity: number;
  readonly maxAcceleration: number;
  readonly maxMach: number;
  /** Duración del vuelo hasta el impacto o el fin de la simulación (s). */
  readonly flightTime: number;
  /** Rapidez en el impacto (m/s); NaN si no hubo impacto. */
  readonly landingVelocity: number;
  /** Rapidez al salir de la varilla (m/s); NaN si no salió. */
  readonly rodClearanceVelocity: number;
  /** Posición de aterrizaje (este, norte) desde el sitio (m). */
  readonly landing: { readonly east: number; readonly north: number };
}

export interface SimulationResult {
  readonly status: SimulationStatus;
  /** Descripción del problema cuando el estado no es `completed`. */
  readonly message?: string;
  readonly events: readonly FlightEvent[];
  readonly data: FlightSeries;
  readonly summary: FlightSummary;
  readonly warnings: readonly string[];
}

export interface SimulationProgress {
  readonly time: number;
  readonly altitude: number;
  readonly phase: FlightPhase;
}

export class SimulationInputError extends Error {
  constructor(
    message: string,
    readonly issues: readonly string[],
  ) {
    super(message);
    this.name = 'SimulationInputError';
  }
}

